import { useCallback } from 'react'
import { LiveConnectionState } from 'types'
import type { TranscriptItem } from 'types'

interface UseVoiceChatArgs {
  connectionState: LiveConnectionState
  isAudioInputActive: boolean
  isAudioInputStarting: boolean
  transcript: TranscriptItem[]
  backendStatus: {
    mode: 'idle' | 'agents' | 'fallback' | 'voice'
    message: string
    severity: 'info' | 'warn' | 'error'
  }
  isChatVisible: boolean
  setIsChatVisible: (visible: boolean) => void
  isWebcamActive: boolean
  setIsWebcamActive: (active: boolean) => void
  handleConnect: () => Promise<void> | void
  handleDisconnect: () => Promise<void> | void
}

export function useVoiceChat({
  connectionState,
  isAudioInputActive,
  isAudioInputStarting,
  transcript,
  backendStatus,
  isChatVisible,
  setIsChatVisible,
  isWebcamActive,
  setIsWebcamActive,
  handleConnect,
  handleDisconnect,
}: UseVoiceChatArgs) {
  const isVoiceConnecting =
    isAudioInputStarting && connectionState !== LiveConnectionState.ERROR
  const isVoiceMode = isAudioInputActive
  const voiceSessionActive = isAudioInputActive || isVoiceConnecting
  const voiceEnabled = voiceSessionActive
  const voiceBusy = voiceSessionActive

  const openChat = useCallback(() => setIsChatVisible(true), [setIsChatVisible])
  const closeChat = useCallback(() => setIsChatVisible(false), [setIsChatVisible])
  const toggleChatVisibility = useCallback(() => setIsChatVisible(!isChatVisible), [isChatVisible, setIsChatVisible])
  const toggleWebcam = useCallback(() => setIsWebcamActive(!isWebcamActive), [isWebcamActive, setIsWebcamActive])

  return {
    isVoiceMode,
    voiceEnabled,
    voiceBusy,
    transcript,
    messageCount: transcript.length,
    backendStatus,
    isChatVisible,
    openChat,
    closeChat,
    toggleChatVisibility,
    isWebcamActive,
    toggleWebcam,
    connectVoice: handleConnect,
    disconnectVoice: handleDisconnect,
  }
}
